import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AiOutlineClose } from 'react-icons/ai';
import * as Yup from 'yup';

import { PSButton } from '@/components/ui/button';
import { AlertsType } from '@/constants/alerts.constant';
import { BlockType, ButtonType } from '@/interfaces/models/Knowledge/scenario.interface';

import DeleteButton from '../../DeleteButton';

export const MacBotButtonSettingForm = ({
  show,
  closeModal,
  addQuickReplies,
  editQuickReplies,
  selectedButtonToEdit,
  quickReplies,
  removeButton,
  tree,
}: {
  show: boolean;
  closeModal: () => void;
  addQuickReplies: (qBtn: any) => void;
  editQuickReplies: (index: number, qRep: any) => void;
  selectedButtonToEdit: number;
  quickReplies: any[];
  removeButton: (index: number) => void;
  tree: any[];
}) => {
  const { t } = useTranslation();

  interface IButtonSetting {
    ButtonTitle: string;
    ButtonType: string;
    Goto?: string;
  }

  const schema = Yup.object({
    ButtonTitle: Yup.string().required(t(AlertsType.ISKBKO022)),
    ButtonType: Yup.string().required(t(AlertsType.ISKBKO023)),
    Goto: Yup.string(),
  });

  const { register, formState, reset, watch, trigger, getValues, setValue, handleSubmit, setError } = useForm<IButtonSetting>({
    defaultValues: {
      ButtonTitle: '',
      ButtonType: 'SEND_MESSAGE',
      Goto: '',
    },
    resetOptions: {
      keepDefaultValues: true,
    },
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (selectedButtonToEdit >= 0) {
      setValue('ButtonTitle', quickReplies[selectedButtonToEdit].title);
      if (quickReplies[selectedButtonToEdit].type > 0) {
        setValue('ButtonType', 'GOTO_BACK');
        setValue('Goto', quickReplies[selectedButtonToEdit].payload.next);
      }
    }
  }, [selectedButtonToEdit]);

  const ButtonSettingSubmit = (data: any) => {
    let payload: any = null;
    let type = 0;
    if (watch('ButtonType') == 'GOTO_BACK') {
      if (watch('Goto') == '') {
        setError('Goto', { type: 'custom', message: t(AlertsType.ISKBKO024) });
        return;
      }
      payload = { next: data.Goto };
      type = ButtonType.Goto;
    } else if (watch('ButtonType') == 'SEND_MESSAGE') {
      payload = { message: data.ButtonTitle };
      type = ButtonType.Message;
    }

    if (selectedButtonToEdit >= 0) {
      editQuickReplies(selectedButtonToEdit, {
        title: data.ButtonTitle,
        type,
        payload,
      });
    } else {
      addQuickReplies({
        title: data.ButtonTitle,
        type,
        payload,
      });
    }
    reset();
    closeModal();
  };

  const deleteHandler = () => {
    if (selectedButtonToEdit >= 0) {
      reset();
      removeButton(selectedButtonToEdit);
      closeModal();
    } else {
      reset();
      closeModal();
    }
  };

  return (
    <>
      {show && (
        <Modal centered show={show} backdrop="static" keyboard={false}>
          <Modal.Body className="p-0">
            <Container className="p-0">
              <Row className="m-0">
                <Col className="tw-flex tw-gap-1 p-3 tw-items-start">
                  <span className="tw-text-neutral-400 tw-text-lg">
                    <svg width="24" viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 9C6.372 9 5 10.372 5 12C5 13.628 6.372 15 8 15C9.628 15 11 13.628 11 12C11 10.372 9.628 9 8 9Z" fill="#FBFCFD" />
                      <path
                        d="M16 6H8C4.7 6 2.011 8.689 2 12V12.016C2.00687 13.6037 2.64172 15.1242 3.76591 16.2454C4.8901 17.3666 6.41228 17.9974 8 18H16C17.5894 17.9974 19.1131 17.3652 20.2376 16.2418C21.3621 15.1185 21.9958 13.5954 22 12.006V12C21.991 8.691 19.301 6 16 6ZM16 16H8C6.94135 15.9984 5.92636 15.5779 5.17685 14.8302C4.42733 14.0826 4.00423 13.0686 4 12.01C4.004 9.799 5.798 8 8 8H16C18.202 8 19.996 9.799 20 12.006C19.9966 13.0653 19.5738 14.0801 18.8243 14.8285C18.0747 15.577 17.0593 15.9982 16 16ZM20 12.016L20.443 12.012L21 12.016H20Z"
                        fill="#889096"
                      />
                    </svg>
                  </span>
                  <span className="tw-text-neutral-400 tw-text-lg font-sofiaSans">{t('MacBotButtonSettingForm/title')}</span>
                </Col>
                <Col>
                  <Row>
                    <Col className="d-flex justify-content-end w-100">
                      <span
                        role="button"
                        tabIndex={0}
                        className="p-3 tw-cursor-pointer"
                        onClick={() => {
                          reset();
                          closeModal();
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            reset();
                            closeModal();
                          }
                        }}
                      >
                        <AiOutlineClose />
                      </span>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="tw-mt-8 ">
                <Col>
                  <Form onSubmit={handleSubmit(ButtonSettingSubmit)}>
                    <Row className="m-0">
                      <Col lg="12" className="px-3">
                        <Form.Group>
                          <Form.Label>{t('MacBotButtonSettingForm/l-01')}</Form.Label>
                          <Form.Control
                            placeholder="Default respond"
                            onFocus={e => (e.target.placeholder = '')}
                            maxLength={60}
                            {...register('ButtonTitle')}
                            onBlur={e => (e.target.placeholder = 'Default respond')}
                          />
                          <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                            {formState.errors.ButtonTitle?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col lg="12">
                        <hr className="tw-bg-gray-200 tw-text-gray-200" />
                      </Col>
                    </Row>
                    <Row className="m-0">
                      <Col lg="12" className="px-3">
                        <Form.Group>
                          <Form.Label>{t('MacBotButtonSettingForm/l-02')}</Form.Label>
                          <Form.Select translate="no" className="color-fifthColor" {...register('ButtonType')}>
                            <option value="SEND_MESSAGE">{t('MacBotButtonSettingForm/text-01')}</option>
                            <option value="GOTO_BACK">{t('MacBotButtonSettingForm/text-02')}</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                            {formState.errors.ButtonType?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col lg="12">
                        <hr className="tw-bg-gray-200 tw-text-gray-200" />
                      </Col>
                    </Row>

                    {watch('ButtonType') == 'GOTO_BACK' && (
                      <Row className="m-0">
                        <Col lg="12" className="px-3">
                          <Form.Group>
                            <Form.Label>{t('MacBotButtonSettingForm/l-03')}</Form.Label>
                            <Form.Select translate="no" className="color-fifthColor" {...register('Goto')}>
                              {tree?.map(dr =>
                                dr.type === BlockType.Response ? (
                                  <option key={dr.id} value={dr.id}>
                                    {dr.title}
                                  </option>
                                ) : null
                              )}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                              {formState.errors.Goto?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col lg="12">
                          <hr className="tw-bg-gray-200 tw-text-gray-200" />
                        </Col>
                      </Row>
                    )}

                    <Row className="m-0 py-3 tw-bg-slate-50">
                      <Col className="tw-flex tw-justify-start tw-items-center">
                        <DeleteButton deleteHandler={deleteHandler} />
                      </Col>
                      <Col className="tw-flex tw-justify-end">
                        <PSButton
                          id="btn-setting-submit"
                          className="tw-min-w-[120px] tw-flex tw-justify-center"
                          type="submit"
                          title={t('MacBotButtonSettingForm/btn-01')}
                        />
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>
            </Container>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};
